import MaterialIcons from '@expo/vector-icons/MaterialIcons';
import { router } from 'expo-router';
import { useEffect, useMemo, useState } from 'react';
import {
  ActivityIndicator,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  View,
  useWindowDimensions,
} from 'react-native';
import { SafeAreaView, useSafeAreaInsets } from 'react-native-safe-area-context';

import { Fonts } from '@/constants/theme';
import { fetchAssignmentsForSchool, type AssignmentListItem } from '@/lib/assignments-admin';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/providers/auth-provider';

type RecentSet = {
  classLabel: string;
  id: string;
  questionCount: number;
  status: 'archived' | 'draft' | 'published';
  subjectLabel: string;
  title: string;
  updatedAt: string;
};

type QuickAction = {
  label: string;
  subtitle: string;
  icon: keyof typeof MaterialIcons.glyphMap;
  route: '/admin/classes' | '/admin/students' | '/admin/teachers';
};

const quickActions: QuickAction[] = [
  {
    label: 'Classes',
    subtitle: 'Manage sections and homerooms',
    icon: 'domain',
    route: '/admin/classes',
  },
  {
    label: 'Students',
    subtitle: 'Add and update learners',
    icon: 'school',
    route: '/admin/students',
  },
  {
    label: 'Teachers',
    subtitle: 'Assign faculty access',
    icon: 'badge',
    route: '/admin/teachers',
  },
];

const statusStyles: Record<
  RecentSet['status'],
  { backgroundColor: string; color: string; label: string }
> = {
  archived: { backgroundColor: '#E1E3E1', color: '#41493E', label: 'Archived' },
  draft: { backgroundColor: '#EAF3FF', color: '#0F4C81', label: 'Draft' },
  published: { backgroundColor: '#E8F5E9', color: '#1B5E20', label: 'Published' },
};

const defaultOverview = {
  assignments: 0,
  classes: 0,
  questionSets: 0,
  questions: 0,
  teachers: 0,
};

function formatToday() {
  return new Intl.DateTimeFormat('en-IN', {
    weekday: 'short',
    day: '2-digit',
    month: 'short',
  }).format(new Date());
}

function getInitials(name: string) {
  const parts = name
    .split(' ')
    .map((value) => value.trim())
    .filter(Boolean);

  if (parts.length === 0) return 'A';

  return parts
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? '')
    .join('');
}

function formatRelativeDate(value: string) {
  const date = new Date(value);
  const now = new Date();
  const diff = Math.max(0, now.getTime() - date.getTime());
  const minutes = Math.floor(diff / 60000);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);

  if (minutes < 1) return 'Just now';
  if (minutes < 60) return `${minutes}m ago`;
  if (hours < 24) return `${hours}h ago`;
  if (days < 7) return `${days}d ago`;

  return date.toLocaleDateString('en-IN', {
    day: '2-digit',
    month: 'short',
  });
}

export default function AdminHomeScreen() {
  const { profile } = useAuth();
  const insets = useSafeAreaInsets();
  const { width } = useWindowDimensions();
  const isDesktop = width >= 960;
  const [overview, setOverview] = useState(defaultOverview);
  const [recentSets, setRecentSets] = useState<RecentSet[]>([]);
  const [assignments, setAssignments] = useState<AssignmentListItem[]>([]);
  const [recentSetsLoading, setRecentSetsLoading] = useState(true);
  const [assignmentsLoading, setAssignmentsLoading] = useState(true);

  const initials = getInitials(profile?.full_name || 'Admin');
  const firstName = profile?.full_name?.split(' ')?.[0] || 'Administrator';
  const todayLabel = formatToday();

  useEffect(() => {
    let isMounted = true;

    if (!profile?.school_id) {
      setOverview(defaultOverview);
      setRecentSets([]);
      setAssignments([]);
      setRecentSetsLoading(false);
      setAssignmentsLoading(false);

      return () => {
        isMounted = false;
      };
    }

    setOverview(defaultOverview);
    setRecentSets([]);
    setAssignments([]);
    setRecentSetsLoading(true);
    setAssignmentsLoading(true);

    const loadOverview = async () => {
      try {
        const [classesResult, questionSetsResult, questionsResult, teacherResult] =
          await Promise.all([
            supabase
              .from('classes')
              .select('id', { count: 'exact', head: true })
              .eq('school_id', profile.school_id),
            supabase
              .from('question_sets')
              .select('id', { count: 'exact', head: true })
              .eq('school_id', profile.school_id),
            supabase
              .from('questions')
              .select('id', { count: 'exact', head: true })
              .eq('school_id', profile.school_id),
            supabase
              .from('profiles')
              .select('id', { count: 'exact', head: true })
              .eq('school_id', profile.school_id)
              .eq('role', 'teacher'),
          ]);

        if (classesResult.error) throw classesResult.error;
        if (questionSetsResult.error) throw questionSetsResult.error;
        if (questionsResult.error) throw questionsResult.error;
        if (teacherResult.error) throw teacherResult.error;

        if (!isMounted) {
          return;
        }

        setOverview((current) => ({
          ...current,
          classes: classesResult.count ?? 0,
          questionSets: questionSetsResult.count ?? 0,
          questions: questionsResult.count ?? 0,
          teachers: teacherResult.count ?? 0,
        }));
      } catch (error) {
        console.error(error);
      }
    };

    const loadAssignments = async () => {
      try {
        const assignmentRows = await fetchAssignmentsForSchool(profile.school_id);

        if (!isMounted) {
          return;
        }

        setAssignments(assignmentRows.slice(0, 4));
        setOverview((current) => ({
          ...current,
          assignments: assignmentRows.length,
        }));
      } catch (error) {
        console.error(error);
      } finally {
        if (isMounted) {
          setAssignmentsLoading(false);
        }
      }
    };

    const loadRecentSets = async () => {
      try {
        const { data, error } = await supabase
          .from('question_sets')
          .select(
            `
            id,
            title,
            status,
            updated_at,
            classes (name, section),
            subjects (name, code)
          `
          )
          .eq('school_id', profile.school_id)
          .order('updated_at', { ascending: false })
          .limit(4);

        if (error) {
          throw error;
        }

        const setRows = (data ?? []) as {
          classes: { name: string; section: string | null }[] | null;
          id: string;
          status: RecentSet['status'];
          subjects: { code: string | null; name: string }[] | null;
          title: string;
          updated_at: string;
        }[];

        const setIds = setRows.map((row) => row.id);
        const questionCounts = new Map<string, number>();

        if (setIds.length > 0) {
          const { data: questionRows, error: questionRowsError } = await supabase
            .from('questions')
            .select('question_set_id')
            .in('question_set_id', setIds);

          if (questionRowsError) {
            throw questionRowsError;
          }

          for (const row of questionRows ?? []) {
            questionCounts.set(
              row.question_set_id,
              (questionCounts.get(row.question_set_id) ?? 0) + 1
            );
          }
        }

        if (!isMounted) {
          return;
        }

        setRecentSets(
          setRows.map((set) => ({
            classLabel: set.classes?.[0]
              ? set.classes[0].section?.trim()
                ? `${set.classes[0].name} - ${set.classes[0].section}`
                : set.classes[0].name
              : 'Class unavailable',
            id: set.id,
            questionCount: questionCounts.get(set.id) ?? 0,
            status: set.status,
            subjectLabel: set.subjects?.[0]
              ? set.subjects[0].code?.trim()
                ? `${set.subjects[0].name} (${set.subjects[0].code})`
                : set.subjects[0].name
              : 'Subject unavailable',
            title: set.title,
            updatedAt: set.updated_at,
          }))
        );
      } catch (error) {
        console.error(error);
      } finally {
        if (isMounted) {
          setRecentSetsLoading(false);
        }
      }
    };

    void loadOverview();

    const stageTwo = requestAnimationFrame(() => {
      void loadAssignments();
      void loadRecentSets();
    });

    return () => {
      isMounted = false;
      cancelAnimationFrame(stageTwo);
    };
  }, [profile?.school_id]);

  const heroStats = useMemo(
    () => [
      { label: 'Classes', value: overview.classes },
      { label: 'Teachers', value: overview.teachers },
      { label: 'Assignments', value: overview.assignments },
    ],
    [overview.assignments, overview.classes, overview.teachers]
  );

  const latestAssignment = assignments[0];
  const latestSet = recentSets[0];
  const snapshotPending = assignmentsLoading || recentSetsLoading;

  if (isDesktop) {
    return (
      <View role="main" style={styles.desktopPage}>
        <ScrollView contentContainerStyle={styles.desktopScroll} showsVerticalScrollIndicator={false}>
          <View style={styles.desktopHero}>
            <View style={styles.desktopHeroTop}>
              <View style={styles.desktopHeroCopy}>
                <Text style={styles.desktopEyebrow}>Admin home</Text>
                <Text style={styles.desktopTitle}>{profile?.full_name || 'Administrator'}</Text>
                <Text style={styles.desktopSubtitle}>{profile?.school_name || 'Admin workspace'}</Text>
              </View>
            </View>

            <View style={styles.desktopHeroPanel}>
              <View style={styles.desktopHeroPanelMain}>
                <Text style={styles.desktopHeroDate}>{todayLabel}</Text>
                <Text role="heading" aria-level={1} style={styles.desktopHeroHeadline}>
                  School operations
                </Text>
                <Text style={styles.desktopHeroBody}>
                  Manage classes, teachers, students, assignments, and sets from one workspace.
                </Text>
              </View>

              <View style={styles.desktopStatsGrid}>
                {heroStats.map((item, index) => (
                  <View
                    key={item.label}
                    style={[styles.desktopStatCard, index > 0 ? styles.desktopStatDivider : null]}>
                    <Text style={styles.desktopStatValue}>{item.value}</Text>
                    <Text style={styles.desktopStatLabel}>{item.label}</Text>
                  </View>
                ))}
              </View>
            </View>
          </View>

          <View style={styles.desktopGrid}>
            <View style={styles.desktopColumnLarge}>
              <View style={styles.desktopSection}>
                <View style={styles.desktopSectionHead}>
                  <View style={styles.desktopSectionCopy}>
                    <Text style={styles.sectionTitle}>Quick actions</Text>
                    <Text style={styles.sectionCaption}>Jump into the next management task.</Text>
                  </View>
                </View>
                <View style={styles.desktopActionGrid}>
                  {quickActions.map((action) => (
                    <Pressable
                      key={action.label}
                      onPress={() => router.push(action.route)}
                      style={styles.desktopActionCard}>
                      <View style={styles.desktopActionIcon}>
                        <MaterialIcons name={action.icon} size={22} color="#163828" />
                      </View>
                      <View style={styles.desktopActionCopy}>
                        <Text style={styles.desktopActionLabel}>{action.label}</Text>
                        <Text style={styles.desktopActionSubtitle}>{action.subtitle}</Text>
                      </View>
                      <MaterialIcons name="arrow-forward-ios" size={14} color="#6F7D71" />
                    </Pressable>
                  ))}
                </View>
              </View>

              <View style={styles.desktopSection}>
                <View style={styles.desktopSectionHead}>
                  <View style={styles.desktopSectionCopy}>
                    <Text style={styles.sectionTitle}>Recent question sets</Text>
                    <Text style={styles.sectionCaption}>Track the latest paper activity from one place.</Text>
                  </View>
                  <Pressable onPress={() => router.push('/admin/assignments')}>
                    <Text style={styles.linkText}>See all</Text>
                  </Pressable>
                </View>
                {recentSetsLoading ? (
                  <View style={styles.loadingWrap}>
                    <ActivityIndicator color="#163828" />
                  </View>
                ) : recentSets.length === 0 ? (
                  <View style={styles.emptyState}>
                    <Text style={styles.emptyTitle}>No recent question sets</Text>
                    <Text style={styles.emptyText}>
                      Create an assignment or question set to populate this area.
                    </Text>
                  </View>
                ) : (
                  <View style={styles.desktopRecentGrid}>
                    {recentSets.map((set, index) => {
                      const badge = statusStyles[set.status];

                      return (
                        <Pressable
                          key={set.id}
                          onPress={() => router.push('/admin/assignments')}
                          style={styles.desktopRecentCard}>
                          <View style={styles.desktopRecentIndex}>
                            <Text style={styles.desktopRecentIndexText}>{`0${index + 1}`}</Text>
                          </View>
                          <View style={styles.desktopRecentCopy}>
                            <Text style={styles.desktopRecentTitle} numberOfLines={1}>
                              {set.title}
                            </Text>
                            <Text style={styles.desktopRecentMeta} numberOfLines={1}>
                              {set.classLabel} - {set.subjectLabel}
                            </Text>
                          </View>
                          <View style={styles.desktopRecentRight}>
                            <Text style={[styles.desktopRecentTag, { color: badge.color }]}>
                              {badge.label}
                            </Text>
                            <Text style={styles.desktopRecentCount}>{set.questionCount} questions</Text>
                          </View>
                        </Pressable>
                      );
                    })}
                  </View>
                )}
              </View>
            </View>

            <View style={styles.desktopColumnSide}>
              <View style={styles.desktopSection}>
                <View style={styles.desktopSectionHead}>
                  <View style={styles.desktopSectionCopy}>
                    <Text style={styles.sectionTitle}>Admin snapshot</Text>
                    <Text style={styles.sectionCaption}>What needs attention right now.</Text>
                  </View>
                </View>
                <View style={styles.desktopSnapshotCard}>
                  <Text style={styles.snapshotLabelOnDark}>Latest assignment</Text>
                  <Text style={styles.desktopSnapshotTitle}>
                    {snapshotPending
                      ? 'Loading dashboard...'
                      : latestAssignment
                      ? latestAssignment.class_label
                      : latestSet
                        ? latestSet.title
                        : 'No assignment lines yet'}
                  </Text>
                  <Text style={styles.desktopSnapshotMeta}>
                    {snapshotPending
                      ? 'Gathering the latest assignments and question set activity.'
                      : latestAssignment
                      ? `${latestAssignment.subject_label} - ${latestAssignment.teacher_label}`
                      : latestSet
                        ? `${latestSet.classLabel} - ${latestSet.subjectLabel}`
                        : 'Connect classes, subjects, and teachers to start using the dashboard.'}
                  </Text>
                  <Pressable onPress={() => router.push('/admin/assignments')} style={styles.snapshotButton}>
                    <Text style={styles.snapshotButtonText}>Open assignments</Text>
                  </Pressable>
                </View>
              </View>

              <View style={styles.desktopSection}>
                <View style={styles.desktopSectionHead}>
                  <View style={styles.desktopSectionCopy}>
                    <Text style={styles.sectionTitle}>Assignments</Text>
                    <Text style={styles.sectionCaption}>Mapped classes, subjects, and teachers.</Text>
                  </View>
                </View>

                {assignmentsLoading ? (
                  <View style={styles.loadingWrap}>
                    <ActivityIndicator color="#163828" />
                  </View>
                ) : assignments.length === 0 ? (
                  <View style={styles.emptyState}>
                    <Text style={styles.emptyTitle}>No assignments yet</Text>
                    <Text style={styles.emptyText}>
                      Link teachers to classes and subjects to populate this panel.
                    </Text>
                  </View>
                ) : (
                  <View style={styles.desktopAssignmentList}>
                    {assignments.map((item, index) => (
                      <View
                        key={item.id}
                        style={[
                          styles.desktopAssignmentRow,
                          index < assignments.length - 1 ? styles.desktopAssignmentDivider : null,
                        ]}>
                        <View style={styles.assignmentLead}>
                          <View style={styles.assignmentDot} />
                          <View>
                            <Text style={styles.assignmentClass}>{item.class_label}</Text>
                            <Text style={styles.assignmentSubject}>{item.subject_label}</Text>
                          </View>
                        </View>
                        <MaterialIcons name="north-east" size={16} color="#6F7D71" />
                      </View>
                    ))}
                  </View>
                )}
              </View>
            </View>
          </View>
          </ScrollView>
      </View>
    );
  }

  return (
    <View role="main" style={styles.container}>
      <SafeAreaView style={styles.safeArea}>
        <ScrollView
          contentContainerStyle={[
            styles.scrollContent,
            { paddingBottom: Math.max(insets.bottom, 20) + 88 },
          ]}
          showsVerticalScrollIndicator={false}>
          <View style={styles.heroShell}>
            <View style={styles.heroTopRow}>
              <Pressable
                onPress={() => router.push('/admin/account')}
                style={({ pressed }) => [styles.identityRow, pressed && styles.pressed]}>
                <View style={styles.avatar}>
                  <Text style={styles.avatarText}>{initials}</Text>
                </View>
                <View style={styles.identityText}>
                  <Text style={styles.eyebrow}>Admin Home</Text>
                  <Text style={styles.name} numberOfLines={1}>
                    {profile?.full_name || 'Administrator'}
                  </Text>
                </View>
              </Pressable>

              <Pressable style={({ pressed }) => [styles.headerIconBtn, pressed && styles.pressed]}>
                <MaterialIcons name="notifications-none" size={22} color="#F4EFE6" />
              </Pressable>
            </View>

            <View style={styles.heroPanel}>
              <View style={styles.heroBody}>
                <Text style={styles.heroKicker}>{todayLabel}</Text>
                <Text role="heading" aria-level={1} style={styles.heroTitle}>
                  Good to see you, {firstName}.
                </Text>
                <Text style={styles.heroSubtitle}>
                  Manage classes, teachers, students, and sets from one screen.
                </Text>
              </View>

              <View style={styles.heroStatsRow}>
                {heroStats.map((item, index) => (
                  <View
                    key={item.label}
                    style={[styles.heroStat, index > 0 ? styles.heroStatDivider : null]}>
                    <Text style={styles.heroStatValue}>{item.value}</Text>
                    <Text style={styles.heroStatLabel}>{item.label}</Text>
                  </View>
                ))}
              </View>
            </View>
          </View>

          <View style={styles.surface}>
            <View style={styles.sectionBlock}>
              <View style={styles.sectionHead}>
                <Text style={styles.sectionTitle}>Quick Actions</Text>
                <Text style={styles.sectionCaption}>Start the next task fast.</Text>
              </View>
              <View style={styles.actionRail}>
                {quickActions.map((action, index) => (
                  <Pressable
                    key={action.label}
                    onPress={() => router.push(action.route)}
                    style={[
                      styles.actionItem,
                      index < quickActions.length - 1 ? styles.actionDivider : null,
                    ]}>
                    <View style={styles.actionIconWrap}>
                      <MaterialIcons name={action.icon} size={20} color="#163828" />
                    </View>
                    <View style={styles.actionCopy}>
                      <Text style={styles.actionLabel}>{action.label}</Text>
                      <Text style={styles.actionSubtitle}>{action.subtitle}</Text>
                    </View>
                    <MaterialIcons name="arrow-forward-ios" size={14} color="#6F7D71" />
                  </Pressable>
                ))}
              </View>
            </View>

            <View style={styles.sectionBlock}>
              <View style={styles.sectionHead}>
                <Text style={styles.sectionTitle}>Admin Snapshot</Text>
                <Text style={styles.sectionCaption}>What needs attention right now.</Text>
              </View>

              <View style={styles.snapshotPanel}>
                <View style={styles.snapshotMain}>
                  <Text style={styles.snapshotLabel}>Latest assignment</Text>
                  <Text style={styles.snapshotValue}>
                    {snapshotPending
                      ? 'Loading dashboard...'
                      : latestAssignment
                      ? latestAssignment.class_label
                      : latestSet
                        ? latestSet.title
                        : 'No assignment lines yet'}
                  </Text>
                  <Text style={styles.snapshotMeta}>
                    {snapshotPending
                      ? 'Gathering the latest assignments and question set activity.'
                      : latestAssignment
                      ? `${latestAssignment.subject_label} - ${latestAssignment.teacher_label}`
                      : latestSet
                        ? `${latestSet.classLabel} - ${latestSet.subjectLabel}`
                        : 'Connect classes, subjects, and teachers to start using the dashboard.'}
                  </Text>
                </View>
                <Pressable
                  onPress={() => router.push('/admin/assignments')}
                  style={styles.snapshotButton}>
                  <Text style={styles.snapshotButtonText}>Open assignments</Text>
                </Pressable>
              </View>
            </View>

            <View style={styles.sectionBlock}>
              <View style={styles.sectionHead}>
                <Text style={styles.sectionTitle}>Recent Sets</Text>
                <Pressable onPress={() => router.push('/admin/assignments')}>
                  <Text style={styles.linkText}>See all</Text>
                </Pressable>
              </View>

              {recentSetsLoading ? (
                <View style={styles.loadingWrap}>
                  <ActivityIndicator color="#163828" />
                </View>
              ) : recentSets.length === 0 ? (
                <View style={styles.emptyState}>
                  <View style={styles.emptyIcon}>
                    <MaterialIcons name="auto-stories" size={28} color="#163828" />
                  </View>
                  <Text style={styles.emptyTitle}>No recent question sets</Text>
                  <Text style={styles.emptyText}>
                    Create a new assignment or question set to see activity here.
                  </Text>
                </View>
              ) : (
                <View style={styles.listBlock}>
                  {recentSets.map((set, index) => {
                    const badge = statusStyles[set.status];

                    return (
                      <Pressable
                        key={set.id}
                        onPress={() => router.push('/admin/assignments')}
                        style={[
                          styles.listRow,
                          index < recentSets.length - 1 ? styles.listDivider : null,
                        ]}>
                        <View style={styles.rowIndex}>
                          <Text style={styles.rowIndexText}>{`0${index + 1}`}</Text>
                        </View>
                        <View style={styles.rowCopy}>
                          <Text style={styles.rowTitle} numberOfLines={1}>
                            {set.title}
                          </Text>
                          <Text style={styles.rowMeta} numberOfLines={1}>
                            {set.classLabel} - {set.subjectLabel}
                          </Text>
                        </View>
                        <View style={styles.rowRight}>
                          <View style={[styles.rowTag, { backgroundColor: badge.backgroundColor }]}>
                            <Text style={[styles.rowTagText, { color: badge.color }]}>
                              {badge.label}
                            </Text>
                          </View>
                          <Text style={styles.rowCount}>{set.questionCount} questions</Text>
                          <Text style={styles.rowDate}>{formatRelativeDate(set.updatedAt)}</Text>
                        </View>
                      </Pressable>
                    );
                  })}
                </View>
              )}
            </View>

            <View style={styles.sectionBlock}>
              <View style={styles.sectionHead}>
                <Text style={styles.sectionTitle}>Assignments</Text>
                <Text style={styles.sectionCaption}>Mapped classes, subjects, and teachers.</Text>
              </View>

              {assignmentsLoading ? (
                <View style={styles.loadingWrap}>
                  <ActivityIndicator color="#163828" />
                </View>
              ) : assignments.length === 0 ? (
                <View style={styles.emptyState}>
                  <Text style={styles.emptyTitle}>No assignments yet</Text>
                  <Text style={styles.emptyText}>
                    Link teachers to classes and subjects to populate this dashboard.
                  </Text>
                </View>
              ) : (
                <View style={styles.assignmentList}>
                  {assignments.map((item, index) => (
                    <Pressable
                      key={item.id}
                      style={[
                        styles.assignmentRow,
                        index < assignments.length - 1 ? styles.listDivider : null,
                      ]}>
                      <View style={styles.assignmentLead}>
                        <View style={styles.assignmentDot} />
                        <View>
                          <Text style={styles.assignmentClass}>{item.class_label}</Text>
                          <Text style={styles.assignmentSubject}>{item.subject_label}</Text>
                        </View>
                      </View>
                      <View style={styles.assignmentRight}>
                        <Text style={styles.assignmentTeacher}>{item.teacher_label}</Text>
                        <MaterialIcons name="north-east" size={16} color="#6F7D71" />
                      </View>
                    </Pressable>
                  ))}
                </View>
              )}
            </View>
          </View>
        </ScrollView>
      </SafeAreaView>
    </View>
  );
}

const styles = StyleSheet.create({
  desktopPage: {
    flex: 1,
    backgroundColor: '#F4EFE6',
  },
  desktopScroll: {
    width: '100%',
    maxWidth: 1480,
    alignSelf: 'center',
    paddingHorizontal: 28,
    paddingTop: 30,
    paddingBottom: 40,
    gap: 26,
  },
  desktopHero: {
    gap: 20,
  },
  desktopHeroTop: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: 20,
  },
  desktopHeroCopy: {
    gap: 8,
    flex: 1,
    minWidth: 0,
    maxWidth: 720,
  },
  desktopEyebrow: {
    color: '#516055',
    fontSize: 11,
    textTransform: 'uppercase',
    letterSpacing: 1.2,
    fontWeight: '800',
  },
  desktopTitle: {
    color: '#163828',
    fontSize: 34,
    lineHeight: 40,
    fontFamily: Fonts.rounded,
    fontWeight: '900',
  },
  desktopSubtitle: {
    color: '#516055',
    fontSize: 14,
    lineHeight: 20,
  },
  desktopHeroPanel: {
    backgroundColor: '#103427',
    borderRadius: 32,
    padding: 26,
    gap: 22,
    borderWidth: 1,
    borderColor: 'rgba(244, 239, 230, 0.09)',
    shadowColor: '#0D261B',
    shadowOffset: { width: 0, height: 14 },
    shadowOpacity: 0.12,
    shadowRadius: 24,
    elevation: 8,
  },
  desktopHeroPanelMain: {
    gap: 10,
    maxWidth: 560,
  },
  desktopHeroDate: {
    color: '#BBD0C2',
    fontSize: 11,
    fontWeight: '800',
    textTransform: 'uppercase',
    letterSpacing: 1.1,
  },
  desktopHeroHeadline: {
    color: '#F7F4EE',
    fontSize: 28,
    lineHeight: 34,
    fontFamily: Fonts.rounded,
    fontWeight: '900',
  },
  desktopHeroBody: {
    color: '#C4D3C9',
    fontSize: 14,
    lineHeight: 21,
    maxWidth: 620,
  },
  desktopStatsGrid: {
    flexDirection: 'row',
    marginTop: 2,
    borderRadius: 22,
    overflow: 'hidden',
    backgroundColor: 'rgba(255, 255, 255, 0.04)',
    borderWidth: 1,
    borderColor: 'rgba(244, 239, 230, 0.08)',
  },
  desktopStatCard: {
    flex: 1,
    paddingHorizontal: 18,
    paddingVertical: 18,
  },
  desktopStatDivider: {
    borderLeftWidth: 1,
    borderLeftColor: 'rgba(244, 239, 230, 0.1)',
  },
  desktopStatValue: {
    color: '#F7F4EE',
    fontSize: 28,
    lineHeight: 32,
    fontFamily: Fonts.rounded,
    fontWeight: '800',
  },
  desktopStatLabel: {
    color: '#9EB5A7',
    fontSize: 12,
    marginTop: 4,
  },
  desktopGrid: {
    flexDirection: 'row',
    gap: 24,
    alignItems: 'flex-start',
  },
  desktopColumnLarge: {
    flex: 1.35,
    gap: 22,
    minWidth: 0,
  },
  desktopColumnSide: {
    flex: 0.88,
    gap: 22,
    minWidth: 0,
  },
  desktopSection: {
    gap: 14,
  },
  desktopSectionHead: {
    flexDirection: 'row',
    alignItems: 'flex-end',
    justifyContent: 'space-between',
    gap: 16,
  },
  desktopSectionCopy: {
    flex: 1,
    gap: 4,
  },
  desktopActionGrid: {
    backgroundColor: '#FBF8F2',
    borderRadius: 24,
    overflow: 'hidden',
    borderWidth: 1,
    borderColor: '#E8DFD3',
  },
  desktopActionCard: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 14,
    paddingHorizontal: 18,
    paddingVertical: 18,
    borderBottomWidth: 1,
    borderBottomColor: 'rgba(22, 56, 40, 0.08)',
  },
  desktopActionIcon: {
    width: 42,
    height: 42,
    borderRadius: 21,
    backgroundColor: '#D9E1D8',
    alignItems: 'center',
    justifyContent: 'center',
  },
  desktopActionCopy: {
    flex: 1,
    gap: 2,
  },
  desktopActionLabel: {
    color: '#163828',
    fontSize: 15,
    fontWeight: '700',
  },
  desktopActionSubtitle: {
    color: '#4F5E54',
    fontSize: 12,
  },
  desktopRecentGrid: {
    gap: 12,
  },
  desktopRecentCard: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 14,
    padding: 18,
    borderRadius: 22,
    backgroundColor: '#FBF8F2',
    borderWidth: 1,
    borderColor: '#E7DED1',
  },
  desktopRecentIndex: {
    width: 38,
    alignItems: 'center',
  },
  desktopRecentIndexText: {
    color: '#7E8F82',
    fontSize: 11,
    fontWeight: '800',
    letterSpacing: 1,
  },
  desktopRecentCopy: {
    flex: 1,
    gap: 3,
    minWidth: 0,
  },
  desktopRecentTitle: {
    color: '#163828',
    fontSize: 15,
    fontWeight: '800',
    fontFamily: Fonts.rounded,
  },
  desktopRecentMeta: {
    color: '#4F5E54',
    fontSize: 12,
  },
  desktopRecentRight: {
    alignItems: 'flex-end',
    gap: 4,
  },
  desktopRecentTag: {
    color: '#163828',
    fontSize: 11,
    textTransform: 'uppercase',
    letterSpacing: 1,
    fontWeight: '800',
  },
  desktopRecentCount: {
    color: '#4F5E54',
    fontSize: 12,
  },
  snapshotLabelOnDark: {
    color: '#D4E4D7',
    fontSize: 11,
    fontWeight: '800',
    letterSpacing: 1.1,
    textTransform: 'uppercase',
    fontFamily: Fonts.sans,
  },
  desktopSnapshotCard: {
    backgroundColor: '#163828',
    borderRadius: 26,
    padding: 22,
    gap: 10,
    minHeight: 190,
    justifyContent: 'space-between',
    shadowColor: '#0D261B',
    shadowOffset: { width: 0, height: 10 },
    shadowOpacity: 0.1,
    shadowRadius: 20,
    elevation: 6,
  },
  desktopSnapshotTitle: {
    color: '#F7F4EE',
    fontSize: 22,
    lineHeight: 28,
    fontFamily: Fonts.rounded,
    fontWeight: '900',
  },
  desktopSnapshotMeta: {
    color: '#C7D5CB',
    fontSize: 13,
    lineHeight: 19,
  },
  desktopAssignmentList: {
    backgroundColor: '#FBF8F2',
    borderRadius: 22,
    overflow: 'hidden',
    borderWidth: 1,
    borderColor: '#E7DED1',
  },
  desktopAssignmentRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: 15,
    paddingHorizontal: 16,
  },
  desktopAssignmentDivider: {
    borderBottomWidth: 1,
    borderBottomColor: 'rgba(22, 56, 40, 0.08)',
  },
  container: {
    flex: 1,
    backgroundColor: '#F4EFE6',
  },
  safeArea: {
    flex: 1,
  },
  scrollContent: {
    paddingHorizontal: 18,
    paddingTop: 10,
    gap: 18,
  },
  heroShell: {
    gap: 18,
  },
  heroTopRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: 14,
  },
  identityRow: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  avatar: {
    width: 48,
    height: 48,
    borderRadius: 16,
    backgroundColor: '#163828',
    alignItems: 'center',
    justifyContent: 'center',
  },
  avatarText: {
    color: '#F4EFE6',
    fontSize: 15,
    fontWeight: '800',
    fontFamily: Fonts.rounded,
  },
  identityText: {
    flex: 1,
    gap: 2,
  },
  eyebrow: {
    color: '#516055',
    fontSize: 11,
    fontWeight: '800',
    letterSpacing: 1.2,
    textTransform: 'uppercase',
    fontFamily: Fonts.sans,
  },
  name: {
    color: '#163828',
    fontSize: 18,
    lineHeight: 22,
    fontWeight: '900',
    fontFamily: Fonts.rounded,
  },
  headerIconBtn: {
    width: 46,
    height: 46,
    borderRadius: 16,
    backgroundColor: '#163828',
    alignItems: 'center',
    justifyContent: 'center',
  },
  heroPanel: {
    backgroundColor: '#163828',
    borderRadius: 28,
    padding: 18,
    gap: 18,
  },
  heroBody: {
    gap: 8,
  },
  heroKicker: {
    color: '#B9CFBE',
    fontSize: 11,
    fontWeight: '800',
    letterSpacing: 1.2,
    textTransform: 'uppercase',
    fontFamily: Fonts.sans,
  },
  heroTitle: {
    color: '#FFFFFF',
    fontSize: 27,
    lineHeight: 33,
    fontWeight: '900',
    fontFamily: Fonts.rounded,
  },
  heroSubtitle: {
    color: '#D4E4D7',
    fontSize: 14,
    lineHeight: 20,
  },
  heroStatsRow: {
    flexDirection: 'row',
    borderRadius: 20,
    overflow: 'hidden',
    backgroundColor: 'rgba(255, 255, 255, 0.08)',
  },
  heroStat: {
    flex: 1,
    alignItems: 'center',
    paddingVertical: 14,
    paddingHorizontal: 10,
    gap: 3,
  },
  heroStatDivider: {
    borderLeftWidth: 1,
    borderLeftColor: 'rgba(255, 255, 255, 0.12)',
  },
  heroStatValue: {
    color: '#FFFFFF',
    fontSize: 20,
    fontWeight: '900',
    fontFamily: Fonts.rounded,
  },
  heroStatLabel: {
    color: '#B9CFBE',
    fontSize: 10,
    fontWeight: '800',
    letterSpacing: 1.1,
    textTransform: 'uppercase',
    fontFamily: Fonts.sans,
  },
  surface: {
    gap: 18,
  },
  sectionBlock: {
    gap: 12,
  },
  sectionHead: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
  },
  sectionTitle: {
    color: '#163828',
    fontSize: 18,
    fontWeight: '900',
    fontFamily: Fonts.rounded,
  },
  sectionCaption: {
    color: '#4F5E54',
    fontSize: 12,
    lineHeight: 16,
    flexShrink: 1,
  },
  linkText: {
    color: '#435146',
    fontSize: 11,
    fontWeight: '800',
    letterSpacing: 1.1,
    textTransform: 'uppercase',
    fontFamily: Fonts.sans,
  },
  actionRail: {
    backgroundColor: '#FFFFFF',
    borderRadius: 22,
    overflow: 'hidden',
  },
  actionItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    padding: 14,
  },
  actionDivider: {
    borderBottomWidth: 1,
    borderBottomColor: '#E8EFE5',
  },
  actionIconWrap: {
    width: 42,
    height: 42,
    borderRadius: 14,
    backgroundColor: '#EEF5EE',
    alignItems: 'center',
    justifyContent: 'center',
  },
  actionCopy: {
    flex: 1,
    gap: 2,
  },
  actionLabel: {
    color: '#163828',
    fontSize: 15,
    fontWeight: '800',
    fontFamily: Fonts.rounded,
  },
  actionSubtitle: {
    color: '#4F5E54',
    fontSize: 12,
    lineHeight: 16,
  },
  snapshotPanel: {
    backgroundColor: '#FFFFFF',
    borderRadius: 22,
    padding: 16,
    gap: 14,
  },
  snapshotMain: {
    gap: 6,
  },
  snapshotLabel: {
    color: '#4A584E',
    fontSize: 11,
    fontWeight: '800',
    letterSpacing: 1.1,
    textTransform: 'uppercase',
    fontFamily: Fonts.sans,
  },
  snapshotValue: {
    color: '#163828',
    fontSize: 20,
    lineHeight: 24,
    fontWeight: '900',
    fontFamily: Fonts.rounded,
  },
  snapshotMeta: {
    color: '#4F5E54',
    fontSize: 13,
    lineHeight: 19,
  },
  snapshotButton: {
    alignSelf: 'flex-start',
    backgroundColor: '#163828',
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderRadius: 14,
  },
  snapshotButtonText: {
    color: '#F4EFE6',
    fontSize: 13,
    fontWeight: '800',
    fontFamily: Fonts.rounded,
  },
  listBlock: {
    backgroundColor: '#FFFFFF',
    borderRadius: 22,
    overflow: 'hidden',
  },
  listRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    padding: 14,
  },
  listDivider: {
    borderBottomWidth: 1,
    borderBottomColor: '#EEF2EA',
  },
  rowIndex: {
    width: 38,
    height: 38,
    borderRadius: 12,
    backgroundColor: '#F0F5F0',
    alignItems: 'center',
    justifyContent: 'center',
  },
  rowIndexText: {
    color: '#163828',
    fontSize: 13,
    fontWeight: '900',
    fontFamily: Fonts.rounded,
  },
  rowCopy: {
    flex: 1,
    gap: 2,
  },
  rowTitle: {
    color: '#163828',
    fontSize: 15,
    fontWeight: '800',
    fontFamily: Fonts.rounded,
  },
  rowMeta: {
    color: '#4F5E54',
    fontSize: 12,
    lineHeight: 16,
  },
  rowRight: {
    alignItems: 'flex-end',
    gap: 6,
  },
  rowTag: {
    borderRadius: 999,
    paddingHorizontal: 8,
    paddingVertical: 4,
  },
  rowTagText: {
    fontSize: 10,
    fontWeight: '800',
    letterSpacing: 0.8,
    textTransform: 'uppercase',
    fontFamily: Fonts.sans,
  },
  rowCount: {
    color: '#435146',
    fontSize: 11,
    fontWeight: '700',
  },
  rowDate: {
    color: '#435146',
    fontSize: 10,
    fontWeight: '700',
    textTransform: 'uppercase',
    letterSpacing: 0.8,
  },
  loadingWrap: {
    backgroundColor: '#FFFFFF',
    borderRadius: 22,
    paddingVertical: 34,
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 1,
    borderColor: '#E8EFE5',
  },
  emptyState: {
    backgroundColor: '#FFFFFF',
    borderRadius: 22,
    paddingVertical: 28,
    paddingHorizontal: 22,
    alignItems: 'center',
    gap: 8,
    borderWidth: 1,
    borderColor: '#E8EFE5',
  },
  emptyIcon: {
    width: 48,
    height: 48,
    borderRadius: 16,
    backgroundColor: '#EEF5EE',
    alignItems: 'center',
    justifyContent: 'center',
  },
  emptyTitle: {
    color: '#163828',
    fontSize: 15,
    fontWeight: '800',
    fontFamily: Fonts.rounded,
  },
  emptyText: {
    color: '#4F5E54',
    fontSize: 13,
    lineHeight: 19,
    textAlign: 'center',
  },
  assignmentList: {
    backgroundColor: '#FFFFFF',
    borderRadius: 22,
    overflow: 'hidden',
  },
  assignmentRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 14,
    padding: 14,
  },
  assignmentLead: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    flex: 1,
  },
  assignmentDot: {
    width: 10,
    height: 10,
    borderRadius: 5,
    backgroundColor: '#163828',
  },
  assignmentClass: {
    color: '#163828',
    fontSize: 15,
    fontWeight: '800',
    fontFamily: Fonts.rounded,
  },
  assignmentSubject: {
    color: '#4F5E54',
    fontSize: 12,
    lineHeight: 16,
  },
  assignmentRight: {
    alignItems: 'flex-end',
    gap: 4,
  },
  assignmentTeacher: {
    color: '#163828',
    fontSize: 12,
    fontWeight: '700',
    maxWidth: 120,
    textAlign: 'right',
  },
  pressed: {
    opacity: 0.82,
    transform: [{ scale: 0.99 }],
  },
});
